import { Phone, ArrowUpRight, Quote } from "lucide-react";
import { Link } from "@/components/common/Link";
const NAVY = "#081438";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const CREAM = "#FAF7F1";
const INK = "#1A1F2E";
const INK_MUTED = "#5A6478";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const FACTS = [{
  num: "20+ yrs",
  label: "Lifespan of a properly installed premium door — decades of daily use",
  tag: "LIFESPAN"
}, {
  num: "7–21 days",
  label: "Lead time from on-site measure to install day — brand-dependent",
  tag: "LEAD TIME"
}, {
  num: "5 types",
  label: "Sectional, roller, tilt, panel-lift, custom — all installed in-house",
  tag: "DOOR TYPES"
}, {
  num: "6+ brands",
  label: "B&D, Steel-Line, Gliderol, Centurion, 4D, Hörmann — same-day quoting",
  tag: "WE INSTALL"
}];
export default function InstallationIntro() {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: CREAM,
    fontFamily: FONT_BODY
  }} aria-labelledby="installation-intro-heading">
      <div aria-hidden className="absolute inset-0 opacity-[0.06] pointer-events-none mix-blend-multiply" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.05 0 0 0 0 0.08 0 0 0 0 0.22 0 0 0 0.6 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"
    }} />

      <div aria-hidden className="absolute top-0 left-0 w-32 h-32 pointer-events-none" style={{
      borderTop: `1px solid ${GOLD}40`,
      borderLeft: `1px solid ${GOLD}40`,
      marginTop: "1.5rem",
      marginLeft: "1.5rem"
    }} />
      <div aria-hidden className="absolute bottom-0 right-0 w-32 h-32 pointer-events-none" style={{
      borderBottom: `1px solid ${GOLD}40`,
      borderRight: `1px solid ${GOLD}40`,
      marginBottom: "1.5rem",
      marginRight: "1.5rem"
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-24 md:pt-32 lg:pt-36 pb-20 md:pb-24 lg:pb-28">

        {/* Eyebrow */}
        <div className="flex items-center justify-between gap-6 mb-12 md:mb-16">
          <div className="flex items-center gap-3">
            <span className="text-[10px] font-bold tracking-[0.3em]" style={{
            color: GOLD_DARK
          }}>
              ·
            </span>
            <span className="text-[11px] font-bold tracking-[0.28em] uppercase" style={{
            color: GOLD_DARK
          }}>
              About Our Door Installations
            </span>
            <span className="hidden sm:block h-px w-16" style={{
            backgroundColor: `${NAVY}26`
          }} />
          </div>
          <div className="text-[10px] font-semibold tracking-[0.22em] uppercase" style={{
          color: INK_MUTED
        }}>
            Measured · Supplied · Installed
          </div>
        </div>

        {/* Headline */}
        <div className="mb-10 md:mb-14">
          <h2 id="installation-intro-heading" className="font-extrabold" style={{
          fontFamily: FONT_HEAD,
          color: NAVY,
          fontSize: "clamp(2.25rem, 6.5vw, 5.25rem)",
          lineHeight: 0.98,
          letterSpacing: "-0.035em"
        }}>
            A garage door isn&rsquo;t a part. <br className="hidden md:block" />
            It&rsquo;s the{" "}
            <span className="relative inline-block">
              <span style={{
              color: GOLD_DARK
            }}>face of your home.</span>
              <span aria-hidden className="absolute left-0 right-0 -bottom-1 h-[3px] md:h-[5px]" style={{
              background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD}00 100%)`,
              borderRadius: "2px"
            }} />
            </span>
          </h2>
        </div>

        <div aria-hidden className="h-px w-full mb-12 md:mb-16" style={{
        background: `linear-gradient(90deg, ${GOLD}80 0%, ${GOLD}40 30%, ${NAVY}14 100%)`
      }} />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* LEFT */}
          <div className="lg:col-span-5 lg:pt-2">
            <p className="text-[18px] md:text-[19px] leading-[1.65]" style={{
            color: INK,
            fontFamily: FONT_BODY
          }}>
              <span className="float-left mr-3 mt-1 font-extrabold leading-none" style={{
              fontFamily: FONT_HEAD,
              color: GOLD_DARK,
              fontSize: "4.5rem",
              lineHeight: 0.85
            }}>
                A
              </span>
              new garage door changes everything. Curb appeal, security, daily
              ritual, even how the house sounds in the morning. We treat the
              install like the long-term decision it is &mdash; measure twice,
              source the right brand for your budget, and finish it like the
              front door.
            </p>

            <p className="mt-6 text-[15px] leading-[1.7]" style={{
            color: INK_MUTED
          }}>
              Sectional, roller, tilt, panel-lift, custom &mdash; every major
              Australian and European brand. Steel-Line, B&amp;D, Gliderol,
              Centurion, 4D Industrial, Hörmann. Insulated, motorised,
              smart-enabled, colour-matched to your render or weatherboard. All
              measured, supplied, installed, and warranted by us.
            </p>

            {/* Quote signature */}
            <div className="mt-8 pt-6 border-t flex items-start gap-3" style={{
            borderColor: `${NAVY}1A`
          }}>
              <Quote className="flex-shrink-0 mt-1 h-5 w-5" strokeWidth={2} style={{
              color: GOLD_DARK
            } as React.CSSProperties} />
              <div>
                <p className="text-[15px] font-medium italic leading-snug" style={{
                color: INK,
                fontFamily: FONT_HEAD
              }}>
                  &ldquo;Measure twice. Install once. Warrant it like it&rsquo;s family&rsquo;s.&rdquo;
                </p>
                <p className="mt-1.5 text-[11px] font-bold tracking-[0.18em] uppercase" style={{
                color: INK_MUTED
              }}>
                  &mdash; The Gold Garage Door Team
                </p>
              </div>
            </div>
          </div>

          {/* RIGHT — facts */}
          <div className="lg:col-span-7">
            <div className="text-[10px] font-bold tracking-[0.32em] uppercase mb-6" style={{
            color: INK_MUTED
          }}>
              &mdash; By the numbers
            </div>

            <dl className="divide-y" style={{
            borderColor: `${NAVY}14`
          }}>
              {FACTS.map(fact => <FactRow key={fact.label} fact={fact} />)}
            </dl>

            {/* CTA card */}
            <div className="mt-10 relative rounded-2xl overflow-hidden p-6 md:p-7" style={{
            background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`
          }}>
              <div aria-hidden className="absolute top-0 left-0 right-0 h-[3px]" style={{
              background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD}80 60%, transparent 100%)`
            }} />

              <div className="flex items-start justify-between gap-6 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <div className="text-[11px] font-bold tracking-[0.22em] uppercase mb-2" style={{
                  color: GOLD
                }}>
                    Free On-Site Measure
                  </div>
                  <p className="text-white text-lg md:text-xl font-bold leading-tight" style={{
                  fontFamily: FONT_HEAD,
                  letterSpacing: "-0.01em"
                }}>
                    No quote without a measure &mdash; that&rsquo;s how we keep it honest.
                  </p>
                </div>

                <Link href="tel:0485000933" className="group inline-flex items-center gap-2.5 rounded-full px-5 py-3 text-[14px] font-bold transition-all duration-200 hover:scale-[1.03] active:scale-[0.98]" style={{
                backgroundColor: GOLD,
                color: NAVY,
                fontFamily: FONT_BODY,
                boxShadow: `0 10px 30px -10px ${GOLD}99`
              }}>
                  <Phone className="h-4 w-4" strokeWidth={2.5} />
                  0485 000 933
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function FactRow({
  fact
}: {
  fact: {
    num: string;
    label: string;
    tag: string;
  };
}) {
  return <div className="group grid grid-cols-12 items-center py-5 md:py-6 transition-colors cursor-default" onMouseEnter={e => {
    e.currentTarget.style.backgroundColor = `${GOLD}08`;
  }} onMouseLeave={e => {
    e.currentTarget.style.backgroundColor = "transparent";
  }}>
      <div className="col-span-2 md:col-span-2">
        <span className="text-[10px] font-bold tracking-[0.22em] uppercase" style={{
        color: INK_MUTED
      }}>
          {fact.tag}
        </span>
      </div>

      <div className="col-span-4 md:col-span-3">
        <div className="font-extrabold" style={{
        fontFamily: FONT_HEAD,
        color: NAVY,
        fontSize: "clamp(1.375rem, 2.4vw, 2rem)",
        lineHeight: 1,
        letterSpacing: "-0.025em",
        fontVariantNumeric: "tabular-nums"
      }}>
          {fact.num}
        </div>
      </div>

      <div className="col-span-6 md:col-span-6 pl-3 md:pl-0">
        <p className="text-[14px] md:text-[15px] leading-snug font-medium" style={{
        color: INK,
        fontFamily: FONT_BODY
      }}>
          {fact.label}
        </p>
      </div>

      <div className="hidden md:flex col-span-1 justify-end">
        <ArrowUpRight className="h-4 w-4 opacity-0 -translate-x-1 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0" strokeWidth={2.5} style={{
        color: GOLD_DARK
      } as React.CSSProperties} />
      </div>
    </div>;
}